import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import ContentHeader from './ContentHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAdmin } from '../contexts/AdminContext';
import LockedWorkspaceBanner from './LockedWorkspaceBanner';
import OnboardingAlert from './OnboardingAlert';
import PostSignupPlanModal from './PostSignupPlanModal';
import AppOfferBanner from './AppOfferBanner';
import { UpgradeModalProvider } from '../contexts/UpgradeModalContext';
import { getWorkspaceIdFromPath } from '../utils/workspaceRoute';

const TemplatesGallery = lazy(() => import('./TemplatesGallery'));

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSidebarVisible, setIsSidebarVisible, isTemplatesPanelOpen, closeTemplatesPanel } = useSidebar();
  const {
    isCurrentWorkspaceLocked,
    currentWorkspace,
    isShopifyEmbedded,
    workspaces,
    loading: workspaceLoading,
    switchWorkspace,
  } = useWorkspace();
  const { isAdmin, impersonatedUserId, setImpersonatedUserId } = useAdmin();
  const previousPathname = useRef<string>(location.pathname);

  // Keep workspace context aligned with `/workspace/:id` in the URL (e.g. after tab sleep / reload)
  useEffect(() => {
    const urlWorkspaceId = getWorkspaceIdFromPath(location.pathname);
    if (!urlWorkspaceId || workspaceLoading) return;
    if (!workspaces.some((w) => w.id === urlWorkspaceId)) return;
    if (currentWorkspace?.id === urlWorkspaceId) return;
    void switchWorkspace(urlWorkspaceId);
  }, [location.pathname, currentWorkspace?.id, workspaces, workspaceLoading, switchWorkspace]);

  // Auto-hide sidebar when navigating to signup form builder or campaign design routes
  useEffect(() => {
    const isSignupFormBuilderRoute = location.pathname.includes('/signup-forms/new') || (location.pathname.includes('/signup-forms/') && location.pathname.includes('/edit'));
    const isCampaignDesignRoute = location.pathname.includes('/campaigns/') && (location.pathname.includes('/design') || location.pathname.includes('/content'));

    const shouldHideSidebar = isSignupFormBuilderRoute || isCampaignDesignRoute;

    if (shouldHideSidebar) {
      if (isSidebarVisible) {
        setIsSidebarVisible(false);
      }
    } else {
      // Auto-show sidebar when on a regular route IF it's hidden (handles restorations after unmount)
      // Only force visible on desktop to avoid popping open on mobile
      if (!isSidebarVisible && window.innerWidth >= 1024) {
        setIsSidebarVisible(true);
      }
    }

    previousPathname.current = location.pathname;
  }, [location.pathname, setIsSidebarVisible, isSidebarVisible]);

  // Navigation guard for locked workspaces
  useEffect(() => {
    if (isCurrentWorkspaceLocked && currentWorkspace) {
      const allowedPaths = [
        `/workspace/${currentWorkspace.id}/dashboard`,
        `/workspace/${currentWorkspace.id}/analytics`,
        `/workspace/${currentWorkspace.id}/contacts`,
        `/workspace/${currentWorkspace.id}/contacts/hygiene`,
        `/workspace/${currentWorkspace.id}/segments`,
        '/discover',
        '/content-manager',
        '/account/billing',
        '/account/profile',
        '/account/settings',
        '/account/developer', // redirects to /workspace/:id/settings/developer
        `/workspace/${currentWorkspace.id}/settings`
      ];

      // Check if current path is allowed (exact match or a sub-path)
      const isAllowed = allowedPaths.some(path =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );

      // If not allowed and we are inside a workspace route, redirect to dashboard
      if (!isAllowed && location.pathname.startsWith(`/workspace/${currentWorkspace.id}`)) {
        navigate(`/workspace/${currentWorkspace.id}/dashboard`);
      }
    }
  }, [isCurrentWorkspaceLocked, currentWorkspace, location.pathname, navigate]);

  const isSignupFormBuilderRoute = location.pathname.includes('/signup-forms/new') || (location.pathname.includes('/signup-forms/') && location.pathname.includes('/edit'));
  const isCampaignDesignRoute = location.pathname.includes('/campaigns/') && (location.pathname.includes('/design') || location.pathname.includes('/content'));
  const isWorkspaceIntegrationsHub = /\/workspace\/[^/]+\/integration$/.test(location.pathname);
  const mainSurfaceClass = isWorkspaceIntegrationsHub
    ? 'bg-gray-50 dark:bg-[#050B12]'
    : 'bg-gray-50 dark:bg-[#162433]';

  // For signup form builder and campaign design: full-screen, no header/sidebar
  if (isSignupFormBuilderRoute || isCampaignDesignRoute) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#162433]">
        <main className="min-h-screen overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-[#162433]">
          {children}
        </main>
      </div>
    );
  }

  return (
    <UpgradeModalProvider>
    <div className="min-h-screen bg-gray-50 dark:bg-[#162433]">
      {/* Mobile Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${isSidebarVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={() => setIsSidebarVisible(false)}
      />

      <div className="flex h-screen bg-gray-50 dark:bg-[#162433]">
        {/* Sidebar */}
        {!isShopifyEmbedded && (
          <div
            className={`flex-shrink-0 fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto w-full lg:w-auto transition-transform duration-300 ease-out lg:transition-none ${isSidebarVisible ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
              } ${!isSidebarVisible ? 'lg:hidden' : ''}`}
          >
            <Sidebar />
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden w-full">
          {/* Header */}
          {!isShopifyEmbedded && <ContentHeader />}

          {!isShopifyEmbedded && <AppOfferBanner />}

          {/* Admin Impersonation Banner */}
          {isAdmin && impersonatedUserId && (
            <div className="bg-amber-100 border-b border-amber-200 px-4 py-3 sm:px-6 lg:px-8 dark:bg-amber-900/30 dark:border-amber-900/50 flex items-center justify-between z-40 relative">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  <span className="font-bold">Admin Mode (read-only):</span> You are viewing user {impersonatedUserId}. Changes are disabled.
                </div>
              </div>
              <button
                onClick={() => {
                  if (setImpersonatedUserId) setImpersonatedUserId(null);
                  navigate('/admin/control-panel-7x9k2m/overview');
                }}
                className="px-3 py-1.5 text-xs font-semibold text-amber-900 bg-amber-200 hover:bg-amber-300 dark:bg-amber-900/50 dark:text-amber-200 dark:hover:bg-amber-900/70 rounded-md transition-colors"
              >
                Exit Admin Mode
              </button>
            </div>
          )}

          {/* Locked Workspace Banner - Only show on workspace-level routes */}
          {location.pathname.startsWith('/workspace/') && <LockedWorkspaceBanner />}

          {/* Main Content Area */}
          <main
            className={`flex-1 overflow-x-hidden overflow-y-auto ${mainSurfaceClass} ${isCurrentWorkspaceLocked && location.pathname.startsWith('/workspace/') && !location.pathname.includes('/settings') ? 'locked-workspace' : ''} ${isWorkspaceIntegrationsHub ? 'no-scrollbar' : ''}`}
          >
            {children}
          </main>
        </div>
      </div>
      
      {/* Onboarding Alerts */}
      <OnboardingAlert />

      <PostSignupPlanModal />

      {/* Templates panel overlay — renders in-place, no route change */}
      {isTemplatesPanelOpen && (
        <Suspense fallback={null}>
          <TemplatesGallery onClose={closeTemplatesPanel} />
        </Suspense>
      )}
    </div>
    </UpgradeModalProvider>
  );
};

export default MainLayout;
